import { createSlice } from '@reduxjs/toolkit';
import history from '../../history';
import { fetchGet, fetchPost, fetchDelete } from '../../helpers/fetching';
import { setSnackbar, refreshProjections } from '../index';

const initialState = {
  totalExaminations: 0,
  currentExam: null,
  exam_list: [],
  priors_list: [],
  currentlyProcessed: null,
  fetchingExamList: false,
  scrollPosition: 0,
  pagination: [0, 40], // list pagination [start, how many]
  orderBy: 'date',
  order: 'DESC',
  pendingReprocessing: false,
  bulkAction: false,
  bulkExaminationsSelected: [],
  worklist: [],
  fetchingWorklist: false,
  worklistInfo: null,
};

export const examinationsSlice = createSlice({
  name: 'examinations',
  initialState,
  reducers: {
    setTotalExaminations: (state, action) => {
      state.totalExaminations = action.payload;
    },
    setCurrentExam: (state, action) => {
      state.currentExam = action.payload;
    },
    setExaminationList: (state, action) => {
      state.exam_list = action.payload;
    },
    setPriorsList: (state, action) => {
      if (action.payload?.replace) {
        state.priors_list = Array.isArray(action.payload.data) ? action.payload.data : [];
      } else {
        const newPriors = Array.isArray(action.payload) ? action.payload : [];
        const existingIds = new Set(state.priors_list.map((prior) => prior.id));
        const uniqueNewPriors = newPriors.filter((prior) => !existingIds.has(prior.id));
        state.priors_list = [...state.priors_list, ...uniqueNewPriors];
      }
    },
    setCurrentlyProcessed: (state, action) => {
      state.currentlyProcessed = action.payload;
    },
    setInitialExamState: () => {
      return { ...initialState };
    },
    setFetchingExamList: (state, action) => {
      state.fetchingExamList = action.payload;
    },
    setPagination: (state, action) => {
      state.pagination = action.payload;
    },
    setSort: (state, action) => {
      state.order = action.payload.order;
      state.orderBy = action.payload.orderBy;
    },
    setPendingReprocessing: (state, action) => {
      state.pendingReprocessing = action.payload;
    },
    setScrollPosition: (state, action) => {
      state.scrollPosition = action.payload;
    },
    setBulkAction: (state, action) => {
      state.bulkAction = action.payload;
    },
    setBulkExaminationsSelected: (state, action) => {
      state.bulkExaminationsSelected = action.payload;
    },
    setWorklist: (state, action) => {
      state.worklist = action.payload;
    },
    setFetchingWorklist: (state, action) => {
      state.fetchingWorklist = action.payload;
    },
    setWorklistInfo: (state, action) => {
      state.worklistInfo = action.payload;
    },
  },
});

export const {
  setTotalExaminations,
  setCurrentExam,
  setCurrentlyProcessed,
  setExaminationList,
  setPriorsList,
  setInitialExamState,
  setFetchingExamList,
  setPagination,
  setPendingReprocessing,
  setScrollPosition,
  setSort,
  setBulkAction,
  setBulkExaminationsSelected,
  setWorklist,
  setFetchingWorklist,
  setWorklistInfo,
} = examinationsSlice.actions;

export default examinationsSlice.reducer;

const getExaminationURL = (pathname, exam_id) => {
  if (pathname.startsWith('/study/')) {
    const pathSegments = pathname.split('/').filter(Boolean);
    if (pathSegments.length >= 3) {
      pathSegments[pathSegments.length - 1] = exam_id;
      return `/${pathSegments.join('/')}`;
    }
  }

  return `/bbox/results/details/${exam_id}`;
};

// Second order
export const showExamination = (exam_id) => {
  return async (dispatch) => {
    dispatch(setCurrentExam(exam_id));

    const { pathname, search, hash } = history.location;
    const newPathname = getExaminationURL(pathname, exam_id);
    const newURL = `${newPathname}${search || ''}${hash || ''}`;
    const currentURL = `${pathname}${search || ''}${hash || ''}`;

    if (currentURL !== newURL) {
      history.push(newURL);
    }
  };
};

export const switchExamResult = (step) => {
  return (dispatch, useState) => {
    let idx = -1;
    const exam_list = useState().examinations.exam_list;
    const currentExam = useState().examinations.currentExam;
    const pagination = useState().examinations.pagination;

    exam_list.some((exam, i) => {
      if (exam.id === currentExam) {
        idx = i;
        return true;
      }
      dispatch(setPagination([pagination[0], pagination[1] + 40]));
      return false;
    });
    if (idx !== -1) {
      let newIdx = idx + step;
      if (newIdx < 0) newIdx = exam_list.length - 1;
      if (newIdx >= exam_list.length) newIdx = 0;
      dispatch(setCurrentExam(exam_list[newIdx].id));
    }
  };
};

export const getNextPatientName = (step) => {
  return (dispatch, useState) => {
    let idx = -1;
    const exam_list = useState().examinations.exam_list;
    const currentExam = useState().examinations.currentExam;

    exam_list.some((exam, i) => {
      if (exam.id === currentExam) {
        idx = i;

        return true;
      }
      return false;
    });
    if (idx !== -1) {
      let newIdx = idx + step;
      if (newIdx < 0) newIdx = exam_list.length - 1;
      if (newIdx >= exam_list.length) newIdx = 0;
      return exam_list[newIdx].name;
    }
  };
};

// Async
export const fetchTotalExaminations = () => {
  return async (dispatch, getState) => {
    const filter = getState().filter;

    const response = await fetchPost('total_exams', { filter }, 'server', false);

    if (!response.success) {
      dispatch(
        setSnackbar({
          msg: `api_messages.${response.msg}`,
          severity: 'error',
        }),
      );
      return;
    }

    if (response.data.n !== getState().examinations.totalExaminations)
      dispatch(setTotalExaminations(response.data.n));
  };
};

export const fetchExaminations = () => {
  return async (dispatch, getState) => {
    dispatch(setFetchingExamList(true));

    const filter = getState().filter;
    const pagination = getState().examinations.pagination;
    const order = getState().examinations.order;
    const orderBy = getState().examinations.orderBy;

    const response = await fetchPost('exams', { filter, limit: pagination, order, orderBy }, 'server', false);

    if (!response.success) {
      dispatch(setFetchingExamList(false));
      dispatch(
        setSnackbar({
          msg: `api_messages.${response.msg}`,
          severity: 'error',
        }),
      );
    } else {
      dispatch(setExaminationList(response.data));
      dispatch(setFetchingExamList(false));
    }
  };
};

export const deleteExams = (exam_ids, t, deleteDBEntries = true) => {
  const username = localStorage.getItem('username');
  const body = {
    study_ids: exam_ids,
    delete_from_db: deleteDBEntries,
    deleted_by: username,
  };
  return async (dispatch, getState) => {
    dispatch(setSnackbar({ msg: 'api_messages.deleting_exams', duration: 4000 }));
    const totalExaminations = getState().examinations.totalExaminations;
    const currentExam = getState().examinations.currentExam;
    const response = await fetchDelete('delete', body, 'bbox');
    if (response.success) {
      if (deleteDBEntries) {
        if (exam_ids.includes(currentExam)) {
          dispatch(switchExamResult(1));
        }
        dispatch(setTotalExaminations(totalExaminations - 1));
      } else {
        dispatch(refreshProjections());
      }

      dispatch(
        setSnackbar({
          msg: 'api_messages.deleted_exam',
          severity: 'success',
        }),
      );
    } else {
      dispatch(
        setSnackbar({
          msg: `api_messages.${response.msg}`,
          severity: 'error',
        }),
      );
    }
  };
};

export const deletePatient = (patient_id, t, deleteDBEntries = true) => {
  const username = localStorage.getItem('username');
  const body = {
    patient_id,
    delete_from_db: deleteDBEntries,
    deleted_by: username,
  };
  return async (dispatch, getState) => {
    dispatch(setSnackbar({ msg: 'api_messages.deleting_patient', severity: 'info', duration: 4000 }));
    const currentExam = getState().examinations.currentExam;
    const totalExaminations = getState().examinations.totalExaminations;
    const response = await fetchDelete('patient_delete', body, 'bbox');
    if (response.success) {
      dispatch(fetchExaminations());
      if (deleteDBEntries) {
        if (!!response.deleted_studies && response.deleted_studies.includes(currentExam)) {
          dispatch(switchExamResult(1));
        }
        dispatch(setTotalExaminations(totalExaminations - 1));
      } else {
        dispatch(refreshProjections());
      }

      dispatch(
        setSnackbar({
          msg: 'api_messages.patient_deleted',
          severity: 'success',
        }),
      );
    } else {
      dispatch(
        setSnackbar({
          msg: `api_messages.${response.msg}`,
          severity: 'error',
        }),
      );
    }
  };
};

// PRIORS ACTIONS

// Search and pull prior examinations in one request
export const fetchAndMovePriorExaminations = (patient_id_list, current_id, t, callback = null) => {
  return async (dispatch, getState) => {
    const response = await fetchPost('find_and_move_priors', { patient_id_list }, 'bbox');

    if (!response.success) {
      if (callback) callback();

      dispatch(
        setSnackbar({
          msg: `api_messages.${response.msg || 'pacs_failed'}`,
          severity: response.msg === 'priors_search_in_progress' ? 'warning' : 'error',
        }),
      );
      return;
    }

    if (Object.keys(response.data).length === 0) {
      if (callback) callback();

      dispatch(
        setSnackbar({
          msg: response.partial ? 'api_messages.priors_search_partial' : 'api_messages.no_priors_found',
          severity: response.partial ? 'warning' : 'info',
        }),
      );
      return;
    }

    const studyIds = Object.keys(response.data);
    const patient_id =
      Array.isArray(patient_id_list) && patient_id_list.length > 0 ? patient_id_list[0] : null;

    const flattenedPriors = studyIds
      .filter((study_id) => !current_id.includes(study_id))
      .map((study_id) => ({
        id: study_id,
        patient_id: patient_id,
      }));

    dispatch(setPriorsList(flattenedPriors));

    studyIds.forEach((id) => {
      if (response.data[id] === 'success') dispatch(removePrior(id));
    });
    if (callback) callback();
    showPriorMoveResults(dispatch, response.data, studyIds, response.partial);
  };
};

export const fetchPriorExaminations = (patient_id_list, t, callback = null) => {
  return async (dispatch, getState) => {
    const response = await fetchPost('find_priors', { patient_id_list }, 'bbox');
    if (!!callback) callback();
    if (!response.success) {
      dispatch(
        setSnackbar({
          msg: `api_messages.${response.msg}`,
          severity: response.msg === 'priors_search_in_progress' ? 'warning' : 'error',
        }),
      );
    } else if (Object.values(response.data).some((priors) => Array.isArray(priors) && priors.length > 0)) {
      const flattenedPriors = [];
      for (const [patient_id, priors] of Object.entries(response.data)) {
        if (Array.isArray(priors)) {
          flattenedPriors.push(
            ...priors.map((prior) => ({
              ...prior,
              patient_id: prior.patient_id || patient_id,
            })),
          );
        }
      }
      dispatch(setPriorsList(flattenedPriors));
      dispatch(
        setSnackbar({
          msg: response.partial ? 'api_messages.priors_search_partial' : 'api_messages.priors_found',
          severity: response.partial ? 'warning' : 'success',
        }),
      );

      // Extract IDs from search prior request and move examinations to b-box
      const studiesToMove = flattenedPriors.map((prior) => prior.id);
      dispatch(movePriorExaminations(studiesToMove, t, callback, true, response.partial));
    } else {
      dispatch(
        setSnackbar({
          msg: response.partial ? 'api_messages.priors_search_partial' : 'api_messages.no_priors_found',
          severity: response.partial ? 'warning' : 'info',
        }),
      );
    }
  };
};

export const removePrior = (study_id) => {
  return async (dispatch, getState) => {
    const currentPriors = getState().examinations.priors_list;
    const filteredPriors = currentPriors.filter((prior) => prior.id !== study_id);
    // Replace the entire array with filtered one
    dispatch(setPriorsList({ replace: true, data: filteredPriors }));
  };
};

const showPriorMoveResults = (dispatch, statuses, studyIds, partialSearch = false) => {
  const results = studyIds.map((studyId) => statuses?.[studyId] || 'failed');
  const success = results.filter((status) => status === 'success').length;
  const failed = results.filter((status) => !['success', 'unconfirmed', 'in_progress'].includes(status)).length;
  const unconfirmed = results.filter((status) => status === 'unconfirmed').length;
  const inProgress = results.filter((status) => status === 'in_progress').length;

  let msg = 'api_messages.priors_moving_complete';
  let severity = 'success';
  if (failed || unconfirmed || inProgress || partialSearch) {
    severity = failed === results.length ? 'error' : 'warning';
    msg = partialSearch ? 'api_messages.priors_move_results_partial' : 'api_messages.priors_move_results';
  }
  dispatch(setSnackbar({ msg, severity, msgParams: { success, failed, unconfirmed, inProgress } }));
};

export const movePriorExaminations = (study_id_list, t, callback, sequentially = false, partialSearch = false) => {
  return async (dispatch, getState) => {
    const studyIds = [...new Set(study_id_list)];
    if (studyIds.length === 0) return;
    const sources = Object.fromEntries(
      getState().examinations.priors_list
        .filter((prior) => studyIds.includes(prior.id) && prior.source_pacs)
        .map((prior) => [prior.id, prior.source_pacs]),
    );
    dispatch(setSnackbar({ msg: 'Settings.moving_priors', severity: 'info' }));
    const statuses = {};
    const batches = sequentially ? studyIds.map((id) => [id]) : [studyIds];
    try {
      for (const batch of batches) {
        try {
          const response = await fetchPost(
            'move_priors', { study_id_list: batch, study_sources: sources }, 'bbox',
          );
          batch.forEach((id) => {
            statuses[id] = response.success ? response.data?.[id] || 'failed' : 'failed';
            if (statuses[id] === 'success') dispatch(removePrior(id));
          });
        } catch (err) {
          console.error('Prior move request failed', err);
          batch.forEach((id) => { statuses[id] = 'failed'; });
        }
      }
    } finally {
      if (callback) callback();
    }
    showPriorMoveResults(dispatch, statuses, studyIds, partialSearch);
  };
};

// Worklist
export const fetchWorklist = (t, manually = false) => {
  return async (dispatch, getState) => {
    dispatch(setFetchingWorklist(true));
    let queryFailed = false;

    try {
      if (manually) {
        const queryResponse = await fetchPost('query_worklist', {}, 'bbox', false);
        if (!queryResponse?.success) {
          queryFailed = true;
        }
      }

      const response = await fetchGet('worklist', 'server', false);

      if (!response?.success) {
        dispatch(
          setSnackbar({
            msg: response?.msg ? `api_messages.${response.msg}` : `${t('Settings.worklist_fetch_failed')}`,
            severity: 'error',
          }),
        );
        return;
      }

      const worklist = Array.isArray(response?.data) ? response.data : [];

      dispatch(setWorklist(worklist));
      dispatch(setWorklistInfo(worklist.length > 0 ? null : 'empty'));

      if (manually) {
        dispatch(
          setSnackbar({
            msg: queryFailed ? `${t('Settings.worklist_fetch_failed')}` : 'Settings.worklist_fetched',
            severity: queryFailed ? 'error' : 'success',
          }),
        );
      }
    } catch (error) {
      console.error('Failed to fetch worklist:', error);

      dispatch(setWorklistInfo(`${t('Settings.worklist_fetch_failed')}: ${error?.message}`));
      if (manually) {
        dispatch(
          setSnackbar({
            msg: `${t('Settings.worklist_fetch_failed')}`,
            severity: 'error',
          }),
        );
      }
    } finally {
      dispatch(setFetchingWorklist(false));
    }
  };
};
